/**
 * Who Am I SFX module — mp3-backed buffer cache, mirrors the WhatIs
 * pattern. Logical cue methods load mp3s from src/assets/sfx and route
 * through audioBus.sfxGain().
 *
 * Cues:
 *   chime    — round opens / new subject
 *   reveal   — per-letter snap reveal
 *   lock     — somebody locked the round
 *   tick     — countdown tick
 *   noWinner — round timeout with no correct guess
 *   fanfare  — game-over podium
 */

import chimeUrl from '../assets/sfx/chime.mp3?url';
import revealUrl from '../assets/sfx/reveal.mp3?url';
import lockUrl from '../assets/sfx/lock.mp3?url';
import tickUrl from '../assets/sfx/tick.mp3?url';
import noWinnerUrl from '../assets/sfx/noWinner.mp3?url';
import fanfareUrl from '../assets/sfx/fanfare.mp3?url';

import { audioBus } from './bus.js';

export type SfxName = 'chime' | 'reveal' | 'lock' | 'tick' | 'noWinner' | 'fanfare';

const SFX_URL_CHIME = chimeUrl;
const SFX_URL_REVEAL = revealUrl;
const SFX_URL_LOCK = lockUrl;
const SFX_URL_TICK = tickUrl;
const SFX_URL_NO_WINNER = noWinnerUrl;
const SFX_URL_FANFARE = fanfareUrl;

function urlFor(name: SfxName): string {
  if (name === 'chime') return SFX_URL_CHIME;
  if (name === 'reveal') return SFX_URL_REVEAL;
  if (name === 'lock') return SFX_URL_LOCK;
  if (name === 'tick') return SFX_URL_TICK;
  if (name === 'noWinner') return SFX_URL_NO_WINNER;
  return SFX_URL_FANFARE;
}

const buffers = new Map<SfxName, AudioBuffer | null>();
const inflight = new Map<SfxName, Promise<AudioBuffer | null>>();

async function loadBuffer(name: SfxName): Promise<AudioBuffer | null> {
  if (buffers.has(name)) return buffers.get(name) ?? null;
  const existing = inflight.get(name);
  if (existing) return existing;
  const ctx = audioBus.context();
  if (!ctx) return null;
  const url = urlFor(name);
  const p = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      buffers.set(name, buf);
      return buf;
    } catch (err) {
      console.warn(`[wai-sfx] failed to load ${name} (${url}):`, err);
      buffers.set(name, null);
      return null;
    } finally {
      inflight.delete(name);
    }
  })();
  inflight.set(name, p);
  return p;
}

function playBuffer(ctx: AudioContext, buf: AudioBuffer, name: SfxName): void {
  try {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(audioBus.sfxGain());
    src.start(0);
  } catch (err) {
    console.warn(`[wai-sfx] play ${name} failed:`, err);
  }
}

function play(name: SfxName): void {
  const ctx = audioBus.context();
  if (!ctx) return;
  const buf = buffers.get(name);
  if (!buf) {
    void loadBuffer(name).then((b) => {
      if (b && audioBus.isUnlocked()) playBuffer(ctx, b, name);
    });
    return;
  }
  if (!audioBus.isUnlocked()) {
    void audioBus.unlock();
  }
  playBuffer(ctx, buf, name);
}

export const sfx = {
  async preload(): Promise<void> {
    const names: SfxName[] = ['chime', 'reveal', 'lock', 'tick', 'noWinner', 'fanfare'];
    await Promise.all(names.map((n) => loadBuffer(n)));
  },
  play,
  chime(): void { play('chime'); },
  reveal(): void { play('reveal'); },
  lock(): void { play('lock'); },
  tick(): void { play('tick'); },
  noWinner(): void { play('noWinner'); },
  fanfare(): void { play('fanfare'); },
};
